import React, { useState, useCallback, useEffect, useRef } from "react";
import { Skeleton, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import ActionCard from "./ActionCard";
import TasksCard from "./TasksCard";
import ReviewPatientsModal from "./ReviewPatientsModal";
import SuccessAnimation from "./SuccessAnimation";
import {
  getPaytientEligiblePatientsAPI,
  bulkSendPaytientAPI,
  getPendingTasksCountsAPI,
  checkShouldPromptForApprovalAPI,
  createAutomaticApprovalAPI,
  getAutomaticApprovalByTypeAPI
} from "../../api/api";
import { selectUser, selectFormPopups } from "../../store/selector";
import { PAYTIENT_PRIMARY } from "../../constant/colors";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import ACTIONS from "../../store/action";
import { ACTION_TYPES, API_ACTION_TYPES, SEND_SOURCES } from "./constants";
import { usePendingActionsPendoTracking } from "../../utils/pendo/usePendingActionsPendoTracking";
import { useGetSetting } from "../../hooks/getSetting";
import "./PendingActions.less";

// Track previous popup state to detect when Tasks popup closes
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const LoadingState = () => (
  <div className="pending-actions-grid">
    {[1, 2, 3].map((item) => (
      <Skeleton.Button key={item} active block style={{ height: 140, borderRadius: 8, minWidth: 280 }} />
    ))}
  </div>
);

const EmptyState = ({ t }) => (
  <div className="pending-actions-empty">
    <CheckCircleOutlined className="empty-icon" />
    <div className="empty-title">{t("pending_actions.empty_state.title", "All caught up!")}</div>
    <div className="empty-description">
      {t(
        "pending_actions.empty_state.description",
        "No pending actions at the moment. Check back later for new patient actions."
      )}
    </div>
  </div>
);

const PendingActions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const formPopups = useSelector(selectFormPopups);

  // Check if pending actions feature is enabled
  const [isPendingActionsEnabled] = useGetSetting(["pending-actions.enabled"]);

  // Pendo tracking
  const {
    trackSectionImpression,
    trackM3PCardImpression,
    trackSendInvitesClick,
    trackSendInvitesSuccess,
    trackReviewPatientsClick,
    trackTasksCardImpression,
    trackViewTasksClick,
    trackAutoApprovalBannerImpression,
    trackAutoApprovalEnableClick,
    trackAutoApprovalDismissClick,
    trackAutoApprovalEnabled
  } = usePendingActionsPendoTracking();
  const [selectedAction, setSelectedAction] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [successAnimation, setSuccessAnimation] = useState({ show: false, actionId: null, count: 0 });
  const cardRefs = useRef({});
  const timeoutRefs = useRef([]);

  // Cleanup timeouts on unmount to prevent memory leaks and state updates on unmounted component
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timerId) => clearTimeout(timerId));
      timeoutRefs.current = [];
    };
  }, []);
  const [paytientData, setPaytientData] = useState({
    patients: [],
    totalCount: 0,
    minThreshold: 5,
    tooltip: ""
  });
  const [tasksData, setTasksData] = useState({
    overdueCount: 0,
    pendingTodayCount: 0,
    totalCount: 0
  });

  // Auto-approval state
  const [showAutoApprovalPrompt, setShowAutoApprovalPrompt] = useState(false);
  const [autoApprovalActionType, setAutoApprovalActionType] = useState(null);
  const [hasActiveAutoApproval, setHasActiveAutoApproval] = useState({});
  const [isEnablingAutoApproval, setIsEnablingAutoApproval] = useState(false);
  const [dismissedAutoApproval, setDismissedAutoApproval] = useState({});

  // Track if Tasks popup was open
  const isTasksPopupOpen = formPopups?.visible && formPopups?.contentKey === formPopupsContentKeys.TASKS;
  const wasTasksPopupOpen = usePrevious(isTasksPopupOpen);

  // Check if an action type has active auto-approval
  const checkAutoApprovalStatus = useCallback(async (actionType) => {
    try {
      const response = await getAutomaticApprovalByTypeAPI(actionType);
      if (response?.data?.success) {
        setHasActiveAutoApproval((prev) => ({
          ...prev,
          [actionType]: response.data.data.hasActiveApproval
        }));
        return response.data.data.hasActiveApproval;
      }
    } catch (error) {
      console.error("Error checking auto-approval status:", error);
    }
    return false;
  }, []);

  // Check if we should prompt for auto-approval after send
  const checkShouldPromptForAutoApproval = useCallback(
    async (actionType) => {
      // Don't prompt if already dismissed in this session
      if (dismissedAutoApproval[actionType]) return false;

      try {
        const response = await checkShouldPromptForApprovalAPI(actionType);
        if (response?.data?.success && response.data.data.shouldPrompt) {
          return true;
        }
      } catch (error) {
        console.error("Error checking auto-approval prompt:", error);
      }
      return false;
    },
    [dismissedAutoApproval]
  );

  // Fetch Paytient eligible patients
  // Note: Defined before handlers that depend on it to avoid TDZ issues
  const fetchPaytientPatients = useCallback(async () => {
    try {
      const response = await getPaytientEligiblePatientsAPI();
      if (response?.data?.success) {
        setPaytientData({
          patients: response.data.data.patients || [],
          totalCount: response.data.data.totalCount || 0,
          minThreshold: response.data.data.minThreshold || 5,
          tooltip: response.data.data.tooltip || ""
        });
      }
    } catch (error) {
      console.error("Error fetching Paytient eligible patients:", error);
      // Keep default empty state on error
    }
  }, []);

  // Helper function for post-send success flow (shared between handleSendToAll and handleSendSelected)
  // Handles checking for auto-approval prompt and refreshing data appropriately
  const handlePostSendSuccess = useCallback(
    async ({ actionType, sentCount }) => {
      // Check if we should prompt for auto-approval FIRST (before refreshing data)
      // This ensures the card is still visible to display the prompt on
      let shouldShowPrompt = false;
      if (actionType && !hasActiveAutoApproval[actionType]) {
        const promptResult = await checkShouldPromptForAutoApproval(actionType);
        shouldShowPrompt = promptResult;
      }

      if (shouldShowPrompt) {
        // Show prompt on the existing card (don't refresh data yet)
        // The prompt handlers will refresh data when dismissed/enabled
        setSuccessAnimation({ show: false, actionId: null, count: 0 });
        message.success(t("pending_actions.send_success", `Successfully sent to ${sentCount} patients`));
        setAutoApprovalActionType(actionType);
        setShowAutoApprovalPrompt(true);
        trackAutoApprovalBannerImpression();
      } else {
        // No prompt needed - refresh data first to prevent flicker, then hide animation
        await fetchPaytientPatients();
        setSuccessAnimation({ show: false, actionId: null, count: 0 });
        message.success(t("pending_actions.send_success", `Successfully sent to ${sentCount} patients`));
      }
    },
    [
      hasActiveAutoApproval,
      checkShouldPromptForAutoApproval,
      fetchPaytientPatients,
      t,
      trackAutoApprovalBannerImpression
    ]
  );

  // Handle enabling auto-approval
  const handleEnableAutoApproval = useCallback(
    async (actionType) => {
      // Track enable auto-approval click
      trackAutoApprovalEnableClick();
      setIsEnablingAutoApproval(true);
      try {
        const response = await createAutomaticApprovalAPI(actionType, {});
        if (response?.data?.success) {
          // Track auto-approval enabled success
          trackAutoApprovalEnabled();
          setHasActiveAutoApproval((prev) => ({
            ...prev,
            [actionType]: true
          }));
          setShowAutoApprovalPrompt(false);
          setAutoApprovalActionType(null);
          // Refresh data to update the card state (card will hide due to hasActiveAutoApproval)
          await fetchPaytientPatients();
          message.success(
            t(
              "auto_approval.enabled_success",
              "Automatic sending enabled! Configure settings in Settings > Automatic Rules."
            )
          );
        } else {
          message.error(t("auto_approval.enable_error", "Failed to enable automatic sending. Please try again."));
        }
      } catch (error) {
        console.error("Error enabling auto-approval:", error);
        message.error(t("auto_approval.enable_error", "Failed to enable automatic sending. Please try again."));
      } finally {
        setIsEnablingAutoApproval(false);
      }
    },
    [t, trackAutoApprovalEnableClick, trackAutoApprovalEnabled, fetchPaytientPatients]
  );

  // Handle dismissing auto-approval prompt
  const handleDismissAutoApproval = useCallback(async () => {
    // Track dismiss auto-approval click
    trackAutoApprovalDismissClick();
    if (autoApprovalActionType) {
      setDismissedAutoApproval((prev) => ({
        ...prev,
        [autoApprovalActionType]: true
      }));
    }
    setShowAutoApprovalPrompt(false);
    setAutoApprovalActionType(null);
    // Refresh data to update the card state (card will disappear since no more eligible patients)
    await fetchPaytientPatients();
  }, [autoApprovalActionType, trackAutoApprovalDismissClick, fetchPaytientPatients]);

  // Fetch pending tasks counts (overdue and pending today)
  const fetchPendingTasks = useCallback(async () => {
    try {
      const response = await getPendingTasksCountsAPI();
      if (response?.data?.success) {
        setTasksData({
          overdueCount: response.data.data.overdueCount || 0,
          pendingTodayCount: response.data.data.pendingTodayCount || 0,
          totalCount: response.data.data.totalCount || 0
        });
      }
    } catch (error) {
      console.error("Error fetching pending tasks counts:", error);
      // Keep default 0 on error
    }
  }, []);

  // Fetch all pending actions data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchPaytientPatients(),
        fetchPendingTasks(),
        checkAutoApprovalStatus(API_ACTION_TYPES.PAYTIENT)
      ]);
      setIsLoading(false);
    };
    fetchAllData();
  }, [fetchPaytientPatients, fetchPendingTasks, checkAutoApprovalStatus]);

  // Track section impression when data is loaded
  const hasTrackedImpression = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      // Track overall section impression
      trackSectionImpression({
        eligiblePatientsCount: paytientData.totalCount,
        minThreshold: paytientData.minThreshold,
        overdueTasksCount: tasksData.overdueCount,
        pendingTodayCount: tasksData.pendingTodayCount
      });
      // Track individual card impressions
      if (paytientData.totalCount >= paytientData.minThreshold) {
        trackM3PCardImpression(paytientData.totalCount, paytientData.totalCount);
      }
      if (tasksData.overdueCount > 0 || tasksData.pendingTodayCount > 0) {
        trackTasksCardImpression(tasksData.overdueCount, tasksData.pendingTodayCount);
      }
    }
  }, [isLoading, paytientData, tasksData, trackSectionImpression, trackM3PCardImpression, trackTasksCardImpression]);

  // Re-fetch tasks counts when Tasks popup closes (user may have completed tasks)
  useEffect(() => {
    // Detect when Tasks popup closes (was open, now closed)
    if (wasTasksPopupOpen && !isTasksPopupOpen) {
      // Re-fetch to get updated counts
      const refreshTasksCounts = async () => {
        try {
          const response = await getPendingTasksCountsAPI();
          if (response?.data?.success) {
            const newOverdueCount = response.data.data.overdueCount || 0;
            const newPendingTodayCount = response.data.data.pendingTodayCount || 0;
            const newTotalCount = newOverdueCount + newPendingTodayCount;
            const previousTotalCount = tasksData.overdueCount + tasksData.pendingTodayCount;

            // If all tasks were completed (count decreased to 0), show success animation
            if (previousTotalCount > 0 && newTotalCount === 0) {
              setSuccessAnimation({
                show: true,
                actionId: ACTION_TYPES.OVERDUE_TASKS,
                count: previousTotalCount,
                label: t("pending_actions.tasks_card.tasks_completed", "tasks completed")
              });

              // After animation, update the counts
              const timerId = setTimeout(() => {
                setTasksData({
                  overdueCount: newOverdueCount,
                  pendingTodayCount: newPendingTodayCount,
                  totalCount: newTotalCount
                });
                setSuccessAnimation({ show: false, actionId: null, count: 0, label: "" });
                message.success(t("pending_actions.tasks_card.all_completed", "Great job! All tasks completed."));
              }, 2500);
              timeoutRefs.current.push(timerId);
            } else {
              // Just update the counts without animation
              setTasksData({
                overdueCount: newOverdueCount,
                pendingTodayCount: newPendingTodayCount,
                totalCount: newTotalCount
              });
            }
          }
        } catch (error) {
          console.error("Error refreshing tasks counts:", error);
        }
      };

      refreshTasksCounts();
    }
  }, [isTasksPopupOpen, wasTasksPopupOpen, tasksData, t]);

  // Build action cards based on API data
  const actionCards = [];

  // Only add Paytient card if totalCount >= minThreshold
  if (paytientData.totalCount >= paytientData.minThreshold) {
    actionCards.push({
      id: ACTION_TYPES.M3P_ELIGIBLE,
      type: ACTION_TYPES.M3P_ELIGIBLE,
      title: t("pending_actions.m3p_eligible.title", "M3P Eligible Patients"),
      description: t("pending_actions.m3p_eligible.description", "Patients eligible for Medicare M3P program"),
      actionLabel: t("pending_actions.m3p_eligible.action", "Send M3P Invite"),
      count: paytientData.totalCount,
      color: PAYTIENT_PRIMARY,
      patients: paytientData.patients,
      tooltip: paytientData.tooltip
    });
  }

  // Future: Add more card types here based on their own API data and thresholds

  // Check if we should show the tasks card
  const showTasksCard = tasksData.overdueCount > 0 || tasksData.pendingTodayCount > 0;
  const isTasksAnimating = successAnimation.show && successAnimation.actionId === ACTION_TYPES.OVERDUE_TASKS;

  const handleSendToAll = useCallback(
    async (action) => {
      if (!action?.patients?.length || !user?.id) return;

      const patientIds = action.patients.map((p) => p.patientId || p.id);
      const actionType = action.type === ACTION_TYPES.M3P_ELIGIBLE ? API_ACTION_TYPES.PAYTIENT : action.type;

      // Track send invites click
      trackSendInvitesClick(patientIds.length);

      try {
        setIsSending(true);
        const response = await bulkSendPaytientAPI(patientIds, SEND_SOURCES.ACTIONS_VIEW);
        const sentCount = response?.data?.data?.sentCount || 0;
        const failedCount = response?.data?.data?.failedCount || 0;

        if (response?.data?.success && sentCount > 0) {
          // Track send success
          trackSendInvitesSuccess(sentCount, failedCount);

          // Trigger success animation
          setSuccessAnimation({ show: true, actionId: action.id, count: sentCount });

          // After animation completes, handle post-send success flow
          const timerId = setTimeout(() => handlePostSendSuccess({ actionType, sentCount }), 2500);
          timeoutRefs.current.push(timerId);
        } else if (sentCount === 0 && failedCount > 0) {
          message.error(t("pending_actions.send_all_failed", `Failed to send to all ${failedCount} patients.`));
        } else {
          message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
        }
      } catch (error) {
        console.error("Error sending to all patients:", error);
        message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
      } finally {
        setIsSending(false);
      }
    },
    [t, user, trackSendInvitesClick, trackSendInvitesSuccess, handlePostSendSuccess]
  );

  const handleReviewPatients = useCallback(
    (action) => {
      // Track review patients click
      trackReviewPatientsClick();
      setSelectedAction(action);
      setModalVisible(true);
    },
    [trackReviewPatientsClick]
  );

  // Handler to open the Tasks popup (same as clicking Tasks in sidebar)
  const handleViewTasks = useCallback(() => {
    // Track view tasks click
    trackViewTasksClick("overdue_or_pending");
    dispatch(ACTIONS.setFormPopups(true, formPopupsContentKeys.TASKS));
  }, [dispatch, trackViewTasksClick]);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedAction(null);
  }, []);

  const handleSendSelected = useCallback(
    async (selectedPatients, action) => {
      if (!selectedPatients?.length || !user?.id) return;

      const patientIds = selectedPatients.map((p) => p.patientId || p.id);
      const actionType = action?.type === ACTION_TYPES.M3P_ELIGIBLE ? API_ACTION_TYPES.PAYTIENT : action?.type;

      // Track send invites click
      trackSendInvitesClick(patientIds.length);

      try {
        setIsSending(true);
        setModalVisible(false); // Close modal before animation
        const response = await bulkSendPaytientAPI(patientIds, SEND_SOURCES.ACTIONS_VIEW);
        const sentCount = response?.data?.data?.sentCount || 0;
        const failedCount = response?.data?.data?.failedCount || 0;

        if (response?.data?.success && sentCount > 0) {
          // Track send success
          trackSendInvitesSuccess(sentCount, failedCount);

          // Trigger success animation
          setSuccessAnimation({ show: true, actionId: action?.id, count: sentCount });

          // After animation completes, handle post-send success flow
          const timerId = setTimeout(() => handlePostSendSuccess({ actionType, sentCount }), 2500);
          timeoutRefs.current.push(timerId);
        } else if (sentCount === 0 && failedCount > 0) {
          message.error(t("pending_actions.send_all_failed", `Failed to send to all ${failedCount} patients.`));
        } else {
          message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
        }
      } catch (error) {
        console.error("Error sending to selected patients:", error);
        message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
      } finally {
        setIsSending(false);
      }
    },
    [t, user, trackSendInvitesClick, trackSendInvitesSuccess, handlePostSendSuccess]
  );

  // Don't render section if feature is disabled
  if (!isPendingActionsEnabled) {
    return null;
  }

  // Calculate visible cards (excluding those hidden by active auto-approval)
  const visibleActionCards = actionCards.filter((action) => {
    const actionType = action.type === ACTION_TYPES.M3P_ELIGIBLE ? API_ACTION_TYPES.PAYTIENT : action.type;
    return !hasActiveAutoApproval[actionType];
  });

  // Don't render section if no cards to display (after loading completes)
  if (!isLoading && visibleActionCards.length === 0 && !showTasksCard) {
    return null;
  }

  return (
    <div className="pending-actions-section">
      <div className="pending-actions-header">
        <div>
          <div className="pending-actions-title">{t("pending_actions.title", "Extra Care Opportunities")}</div>
          <div className="pending-actions-subtitle">
            {t("pending_actions.subtitle", "Help more patients save on their medications")}
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : visibleActionCards.length === 0 && !showTasksCard ? (
        <EmptyState t={t} />
      ) : (
        <div className="pending-actions-grid">
          {visibleActionCards.map((action) => {
            const isAnimating = successAnimation.show && successAnimation.actionId === action.id;
            const actionType = action.type === ACTION_TYPES.M3P_ELIGIBLE ? API_ACTION_TYPES.PAYTIENT : action.type;

            // Determine if we should show the auto-approval banner for this card
            const shouldShowBanner = showAutoApprovalPrompt && autoApprovalActionType === actionType;

            return (
              <div
                key={action.id}
                ref={(el) => (cardRefs.current[action.id] = el)}
                className={`action-card-wrapper ${isAnimating ? "success-animation" : ""}`}
              >
                {isAnimating ? (
                  <SuccessAnimation count={successAnimation.count} />
                ) : (
                  <ActionCard
                    action={action}
                    onSendToAll={handleSendToAll}
                    onReviewPatients={handleReviewPatients}
                    onViewTasks={handleViewTasks}
                    disabled={isSending}
                    showAutoApprovalBanner={shouldShowBanner}
                    onEnableAutoApproval={() => handleEnableAutoApproval(actionType)}
                    onDismissAutoApproval={handleDismissAutoApproval}
                    isEnablingAutoApproval={isEnablingAutoApproval}
                  />
                )}
              </div>
            );
          })}

          {/* Tasks Card - separate from ActionCards */}
          {(showTasksCard || isTasksAnimating) && (
            <div
              ref={(el) => (cardRefs.current[ACTION_TYPES.OVERDUE_TASKS] = el)}
              className={`action-card-wrapper ${isTasksAnimating ? "success-animation" : ""}`}
            >
              {isTasksAnimating ? (
                <SuccessAnimation
                  count={successAnimation.count}
                  label={successAnimation.label || t("pending_actions.tasks_card.tasks_completed", "tasks completed")}
                />
              ) : (
                <TasksCard
                  overdueCount={tasksData.overdueCount}
                  pendingTodayCount={tasksData.pendingTodayCount}
                  onViewTasks={handleViewTasks}
                  disabled={isSending}
                />
              )}
            </div>
          )}
        </div>
      )}

      <ReviewPatientsModal
        visible={modalVisible}
        action={selectedAction}
        onClose={handleCloseModal}
        onSendSelected={handleSendSelected}
        isSending={isSending}
      />
    </div>
  );
};

export default PendingActions;
